import { useCallback, useEffect, useState } from "react";

import { useParams } from "react-router-dom";

import {
	TAvailability,
	TCourse,
	TLesson,
	TPolicy,
} from "../../../../../@types";
import { apis, callApi } from "../../../../../api";
import { courseType } from "../../../../../constants";

export type TCheckoutCourseService = {
	listeners: (() => void)[];
	savedCourse: TCourse | null;
	owned: boolean;
	addEventListener: (cb: () => void) => void;
	saveCourse: (newCourse: TCourse, owned: boolean) => void;
	clearData: () => void;
	emitChanges: () => void;
	removeEventListener: (cb: () => void) => void;
};

export const CheckoutCourseService: TCheckoutCourseService = {
	listeners: [],
	savedCourse: null,
	owned: false,

	addEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
		this.listeners.push(cb);
	},

	saveCourse(newCourse, owned) {
		this.savedCourse = newCourse;
		this.owned = owned;
		this.emitChanges();
	},

	clearData() {
		this.savedCourse = null;
		this.emitChanges();
	},

	/**
	 * Send new course data to all listeners
	 */
	emitChanges() {
		for (const listener of this.listeners) {
			listener();
		}
	},

	removeEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
	},
};

type TBooking = {
	id: number;
	startTime: string;
	endTime: string;
	lessonId: number;
	lesson: TLesson;
};

type TBusyTime = {
	id: number;
	startTime: string;
	endTime: string;
};

export type TBookingCourseService = {
	listeners: (() => void)[];
	availableHours: TAvailability[];
	bookings: TBooking[];
	busyTimes: TBusyTime[];
	policy: TPolicy | null;
	addEventListener: (cb: () => void) => void;
	initData: (
		availableHours: TAvailability[],
		bookings: TBooking[],
		policy: TPolicy,
	) => void;
	saveBusyTimes: (busyTime: TBusyTime[]) => void;
	clearData: () => void;
	emitChanges: () => void;
	removeEventListener: (cb: () => void) => void;
};

export const BookingCourseService: TBookingCourseService = {
	listeners: [],
	availableHours: [],
	bookings: [],
	busyTimes: [],
	policy: null,

	addEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
		this.listeners.push(cb);
	},

	initData(availableHours, bookings, policy) {
		this.availableHours = availableHours;
		this.bookings = bookings;
		this.policy = policy;
		this.emitChanges();
	},

	saveBusyTimes(busyTimes) {
		this.busyTimes = busyTimes;
		this.emitChanges();
	},

	clearData() {
		this.availableHours = [];
		this.bookings = [];
		this.policy = null;
		this.emitChanges();
	},

	/**
	 * Send new course data to all listeners
	 */
	emitChanges() {
		for (const listener of this.listeners) {
			listener();
		}
	},

	removeEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
	},
};

/**
 * This hook should call only once
 */
export function useCheckoutCourseInit() {
	const params = useParams();
	const slug = params.slug ?? "";

	const [isNotFound, setIsNotFound] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	const getSchedule = async (teacherId: number) => {
		try {
			const response = await callApi({
				method: "GET",
				url: `${apis.CLASSES_SCHEDULE}/${teacherId}`,
			});

			if (!response) {
				return;
			}

			const { availableHours, bookings, policy } = response;
			BookingCourseService.initData(availableHours, bookings, policy);
		} catch (error) {}
	};

	const getBusy = async (teacherId: number) => {
		try {
			const response = await callApi({
				method: "GET",
				url: `${apis.GET_INSTRUCTOR_BUSY_TIME}/${teacherId}`,
			});

			if (!response?.events) {
				BookingCourseService.saveBusyTimes([]);
				return;
			}

			BookingCourseService.saveBusyTimes(response.events);
		} catch (error) {
			BookingCourseService.saveBusyTimes([]);
		}
	};

	/**
	 * Get course by slug
	 * @param {string} slug
	 */
	const getCourseBySlug = useCallback(async () => {
		try {
			const response = await callApi({
				method: "GET",
				url: `${apis.GET_COURSE_DETAIL}/${slug}`,
			});

			if (!response?.course) {
				return;
			}

			CheckoutCourseService.saveCourse(response.course, response.owned);

			if (response.course.type === courseType.ONSITE) {
				const promiseGetSchedule = getSchedule(response.course.teacherId);
				const promiseGetBusyTimes = getBusy(response.course.teacherId);
				await promiseGetSchedule;
				await promiseGetBusyTimes;
			}
		} catch (error: any) {
			if (error?.response?.status === 404) {
				setIsNotFound(true);
			}
		} finally {
			setIsLoading(false);
		}
	}, [slug]);

	useEffect(() => {
		getCourseBySlug();
	}, [getCourseBySlug]);

	useEffect(() => {
		return () => {
			CheckoutCourseService.clearData();
			BookingCourseService.clearData();
		};
	}, []);

	return { isNotFound, isLoading };
}

export function useCheckoutCourse() {
	const [course, setCourse] = useState(CheckoutCourseService.savedCourse);
	const [owned, setOwned] = useState(CheckoutCourseService.owned);

	useEffect(() => {
		const updateCourse = () => {
			setCourse(CheckoutCourseService.savedCourse);
			setOwned(CheckoutCourseService.owned);
		};

		CheckoutCourseService.addEventListener(updateCourse);

		return () => {
			CheckoutCourseService.removeEventListener(updateCourse);
		};
	}, []);

	return { course, owned };
}

export function useBookingCourse() {
	const [availableHours, setAvailableHours] = useState(
		BookingCourseService.availableHours,
	);
	const [bookings, setBookings] = useState(BookingCourseService.bookings);
	const [policy, setPolicy] = useState(BookingCourseService.policy);
	const [busyTimes, setBusyTimes] = useState(BookingCourseService.busyTimes);

	useEffect(() => {
		const updateBooking = () => {
			setAvailableHours(BookingCourseService.availableHours);
			setBookings(BookingCourseService.bookings);
			setPolicy(BookingCourseService.policy);
			setBusyTimes(BookingCourseService.busyTimes);
		};

		BookingCourseService.addEventListener(updateBooking);

		return () => {
			BookingCourseService.removeEventListener(updateBooking);
		};
	}, []);

	return { availableHours, bookings, policy, busyTimes };
}
